import { FC, useEffect, useState } from 'react';
import Colours from '../../../design-system/colours';
import styled from 'styled-components';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import breakpoints from '../../../design-system/breakpoints';
import { FormikProvider, useFormik } from 'formik';
import { CreateNotificationContentRequest } from '../../../common/model/dto/notification/create-notification-content-request';
import {
  NotificationActionType,
  NotificationButtonStyle,
  NotificationContent,
  NotificationContentCategory,
} from '../../../common/model/dto/notification/notification-content';
import { useTranslation } from 'react-i18next';
import OvDeviceView from './OvDeviceView';
import OvNotificationContentDevicePreview from './OvNotificationContentDevicePreview';
import OvNotificationContentInfoStep from './OvNotificationContentInfoStep';
import OvNotificationContentActionsStep from './OvNotificationContentActionsStep';
import OvNotificationContentConditionsStep from './OvNotificationContentConditionsStep';
import { NotificationContentDataUtils } from '../../../common/utils/services/notification-content-utils';

interface OvCreateSymptomLoggedEventNotificationContentDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (request: any) => void;
  notificationContent?: NotificationContent;
}

export interface SymptomConditionSetForm {
  pairs: Array<{ key: string; value: string }>;
}

export interface EventConditionsForm {
  os?: string | null;
  health_goal?: string | null;
  symptom_conditions: SymptomConditionSetForm[];
}
const OvCreateSymptomLoggedEventNotificationContentDialog: FC<
  OvCreateSymptomLoggedEventNotificationContentDialogProps
> = ({ isOpen, onCancel, onSave, notificationContent }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    'Notification content',
    'Action button configurations',
    'Conditions',
  ];

  const formik = useFormik<CreateNotificationContentRequest>({
    enableReinitialize: true,
    initialValues: {
      title: notificationContent?.title,
      category:
        notificationContent?.category ?? NotificationContentCategory.FROM_OOVA,
      image_url: notificationContent?.image_url,
      thumbnail_image_url: notificationContent?.thumbnail_image_url,
      actions: notificationContent?.actions ?? [
        {
          cta: '',
          button_style: NotificationButtonStyle.PRIMARY,
          action_url: '',
          type: NotificationActionType.accept,
        },
      ],
      short_description: notificationContent?.short_description,
      full_description: notificationContent?.full_description,
      disclaimer: notificationContent?.disclaimer,
      tags: notificationContent?.tags ?? [],
      params: notificationContent?.params,
      app_version: notificationContent?.app_version,
      event_type: 'user.symptom-logged',
      event_conditions: notificationContent
        ? NotificationContentDataUtils.mapBackendConditionsToFormikConditions(
            notificationContent.event_conditions
          )
        : {
            os: '',
            health_goal: [],
            symptom_conditions: [
              { pairs: [{ key: '', value: '', operator: '' }] },
            ],
          },
      is_published: notificationContent?.is_published ?? true,
    },
    onSubmit: (values) => {
      const transformed = NotificationContentDataUtils.createPayload(values);
      onSave(transformed);
      handleOnCancel();
    },
  });

  useEffect(() => {
    if (isOpen && !notificationContent) {
      formik.resetForm();
      setActiveStep(0);
    }
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    formik.getFieldHelpers('insights_type').setValue(undefined);
    formik.getFieldHelpers('params').setValue(undefined);
    formik.getFieldHelpers('app_version').setValue(undefined);

    // eslint-disable-next-line
  }, [formik.values.category]);

  const handleNextClick = () => {
    setActiveStep((step) => step + 1);
  };

  const handleBackClick = () => {
    setActiveStep((step) => step - 1);
  };

  const handleOnCancel = () => {
    formik.resetForm();
    onCancel();
    setActiveStep(0);
  };

  const handleSuccessfulImageUpload = ({
    image_path,
    resized_image_path,
  }: {
    image_path: string;
    resized_image_path: string;
  }) => {
    formik.getFieldHelpers('image_url').setValue(image_path);
    formik.getFieldHelpers('thumbnail_image_url').setValue(resized_image_path);
  };

  return (
    <StyledDialog open={isOpen} onClose={onCancel}>
      <FormikProvider value={formik}>
        <StyledDialogTitle>
          {notificationContent
            ? t(
                'notificationContents.dialogs.editNotificationContentForSymptomLoggedEvent'
              )
            : t(
                'notificationContents.dialogs.createNotificationContentForSymptomLoggedEvent'
              )}
        </StyledDialogTitle>
        <StyledStepper activeStep={activeStep}>
          {steps.map((label) => {
            const stepProps: { completed?: boolean } = {};

            return (
              <Step key={label} {...stepProps}>
                <StyledStepLabel>{label}</StyledStepLabel>
              </Step>
            );
          })}
        </StyledStepper>

        <ContentWrapper>
          {activeStep === 0 && (
            <OvNotificationContentInfoStep
              formik={formik}
              onNext={handleNextClick}
              onCancel={handleOnCancel}
              onImageUpload={handleSuccessfulImageUpload}
            />
          )}

          {activeStep === 1 && (
            <OvNotificationContentActionsStep
              formik={formik}
              onNext={handleNextClick}
              onCancel={handleOnCancel}
              onBack={handleBackClick}
            />
          )}

          {activeStep === 2 && (
            <OvNotificationContentConditionsStep
              formik={formik}
              onBack={handleBackClick}
              onCancel={handleOnCancel}
              submitButtonCta={
                notificationContent
                  ? t('common.actions.edit')
                  : t('common.actions.create')
              }
            />
          )}

          <StyledDialogContent>
            <DeviceViewContainer>
              <OvDeviceView>
                <OvNotificationContentDevicePreview
                  notificationContentDetails={formik.values}
                />
              </OvDeviceView>
            </DeviceViewContainer>
          </StyledDialogContent>
        </ContentWrapper>
      </FormikProvider>
    </StyledDialog>
  );
};

export default OvCreateSymptomLoggedEventNotificationContentDialog;

const StyledDialog = styled(Dialog)`
  && {
    .MuiDialog-paperScrollPaper {
      max-width: 100%;
      background-color: ${Colours.OV_WHITE};
    }
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  && {
    text-align: center;
    font-weight: bold;
    text-transform: none;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    width: 100%;
    min-width: 31.25rem !important;
    padding: 0.5rem 1.5rem !important;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 0.75rem;

    @media (min-width: ${breakpoints.sm}) {
      width: 34rem;
    }
  }
`;

const StyledStepper = styled(Stepper)`
  && {
    padding: 1rem;
  }
`;

const StyledStepLabel = styled(StepLabel)`
  && {
    .Mui-active {
      color: ${Colours.OV_BASE};
    }

    .Mui-completed {
      color: ${Colours.OV_GREEN};
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
`;

const DeviceViewContainer = styled.div`
  min-width: 23rem;
  max-width: 100%;
  height: 41rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem;
`;
